/**
 * Back-in-stock sign-up (Klaviyo Back in Stock).
 * - PDP card: snippets/product-restock-signup.liquid, follows the variant picker (on:variant:change).
 * - Collection card: snippets/product-card-restock.liquid, a "Notify me" button that reveals the form.
 * Events are delegated from the document so cards added later (filters, pagination) work too.
 */
const ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const ERROR_TEXT = 'Something went wrong, please try again.';
const INVALID_TEXT = 'Please enter a valid email address.';

function setStatus(card, text, isError) {
  const status = card.querySelector('.restock-card__status');
  status.textContent = text;
  status.classList.toggle('is-error', Boolean(isError));
}

function reset(card) {
  card.classList.remove('is-done');
  card.querySelector('.restock-card__input').removeAttribute('aria-invalid');
  setStatus(card, '');
}

async function subscribeVariant(key, variantId, email) {
  const res = await fetch(`${ENDPOINT}?company_id=${encodeURIComponent(key)}`, {
    method: 'POST',
    headers: { 'content-type': 'application/vnd.api+json', revision: '2024-10-15' },
    body: JSON.stringify({
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: { data: { type: 'profile', attributes: { email } } },
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
        },
      },
    }),
  });
  if (!res.ok) throw new Error(`Klaviyo ${res.status}`);
}

// Collection cards pass every sold-out variant (data-variant-ids); the PDP passes the selected one.
function subscribe(card, email) {
  const ids = (card.dataset.variantIds || card.dataset.variantId || '').split(',').filter(Boolean);
  return Promise.all(ids.map((id) => subscribeVariant(card.dataset.klaviyoKey, id, email)));
}

async function onSubmit(evt) {
  const form = evt.target.closest('.restock-card__form');
  const card = form && form.closest('[data-restock]');
  if (!card) return;
  evt.preventDefault();

  const input = card.querySelector('.restock-card__input');
  const button = card.querySelector('.restock-card__btn');
  if (button.classList.contains('is-loading')) return;

  if (!input.checkValidity()) {
    input.setAttribute('aria-invalid', 'true');
    setStatus(card, INVALID_TEXT, true);
    input.focus();
    return;
  }

  input.removeAttribute('aria-invalid');
  setStatus(card, '');
  button.classList.add('is-loading');
  button.setAttribute('aria-disabled', 'true');

  try {
    const email = input.value.trim();
    await subscribe(card, email);
    card.classList.add('is-done');
    // Echo the address back ({email}) so a typo in a long address is easy to spot.
    setStatus(card, card.querySelector('.restock-card__status').dataset.success.replace('{email}', email));
  } catch (err) {
    setStatus(card, ERROR_TEXT, true);
  } finally {
    button.classList.remove('is-loading');
    button.removeAttribute('aria-disabled');
  }
}

function onClick(evt) {
  const opener = evt.target.closest('.restock-card__open');
  const card = opener && opener.closest('[data-restock]');
  if (!card) return;
  evt.preventDefault();
  card.classList.add('is-open');
  opener.setAttribute('aria-expanded', 'true');
  card.querySelector('.restock-card__form').hidden = false;
  // Logged-in customers get their email prefilled, so jump straight to the submit button.
  const input = card.querySelector('.restock-card__input');
  (input.value ? card.querySelector('.restock-card__btn') : input).focus();
}

function onInput(evt) {
  const input = evt.target.closest('.restock-card__input');
  if (input && input.hasAttribute('aria-invalid')) reset(input.closest('[data-restock]'));
}

function onVariantChange(evt) {
  const variant = evt.detail && evt.detail.variant;
  if (!variant) return;
  // Only the PDP card follows the picker; collection cards are fixed to their own product.
  document.querySelectorAll('[data-restock]:not([data-restock-card])').forEach((card) => {
    if (String(card.dataset.variantId) === String(variant.id)) return;
    card.dataset.variantId = variant.id;
    reset(card);
    if (card.dataset.unavailableOnly === 'true') card.hidden = Boolean(variant.available);
  });
}

if (!window.restockSignupReady) {
  window.restockSignupReady = true;
  document.addEventListener('submit', onSubmit);
  document.addEventListener('click', onClick);
  document.addEventListener('input', onInput);
  document.addEventListener('on:variant:change', onVariantChange);
}
